import { Button } from '@fox/atoms/src/button/index';
import { LabeledCheckbox } from '@fox/atoms/src/labeled-checkbox/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { FlexColumnSmallSpaced, SpaceBetweenRow } from '@fox/layout/src/flex';
import { CREATE_PEOPLE_BLOCK } from '../../../../form-block-types';
import { ReservationUpdate } from '../../../form-event-context/contracts';
import { useFormEventContext } from '../../../form-event-context/form-event-context';
import { FormRenderMode } from '../../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { QuestionDescriptionRender } from '../../question-block/question-description';
import { QuestionLabelRender } from '../../question-block/question-label';
import { BUSINESS_ADDRESS_FIELD, BUSINESS_NAME_FIELD, CreatePersonBlockMetadata } from '../contracts';
import { getFieldOrder } from '../getFieldOrder';
import { CustomFieldLink } from '../shared-components/custom-field-link';
import { CustomFieldSelect } from '../shared-components/custom-field-select';
import { CustomFieldText } from '../shared-components/custom-field-text';
import { QuestionField } from '../shared-components/question-field';
import { getRenderField } from '../shared-components/render-fields';
import { getBusinessAddresses, updateAnswerForContact, updateBusinessDetailsForContact } from '../util';

type MultiPersonSectionProps = {
	id: string;
	type: string;
	renderQuestionLabel: boolean;
	currentAnswerSelections: any;
	currentAnswer: any;
	fieldProps: any;
	contact?: {
		id: string;
		asAbove?: boolean;
		businessName?: string;
		businessRole?: string;
		[key: string]: string | boolean;
	};
};

export const MultiPersonSection = (props: MultiPersonSectionProps) => {
	const { currentAnswer, currentAnswerSelections, id, type, contact, fieldProps } = props;

	const { formRenderMode, answerQuestion } = useFormRuntimeContext();
	const eventContext = useFormEventContext();

	const metaData = tryParseJSONObject<CreatePersonBlockMetadata>(fieldProps?.metadataJson, {});

	return (
		<>
			{props.renderQuestionLabel ? (
				<QuestionLabelRender id={id} style={{ fontSize: '1.25rem', padding: 0 }} value={fieldProps?.title} />
			) : (
				<></>
			)}
			<FlexColumnSmallSpaced>
				{getFieldOrder(metaData)
					.filter(o =>
						(currentAnswerSelections?.['businessDetails']?.['allContactsFromBusiness'] ||
							currentAnswerSelections?.contacts?.length === 1) &&
						metaData.businessSection
							? !o.startsWith(BUSINESS_ADDRESS_FIELD) && o !== BUSINESS_NAME_FIELD
							: true
					)
					?.map(f => {
						const businessField = metaData.businessFields?.[f];
						if (businessField) {
							return (
								<FlexColumnSmallSpaced key={businessField.id}>
									<QuestionDescriptionRender
										id={id}
										required={businessField?.meta?.required}
										value={businessField.labelOverride ?? businessField.label}
									/>

									{businessField.type === 'select' ? (
										<CustomFieldSelect
											id={id}
											currentAnswer={currentAnswer}
											currentAnswerSelections={currentAnswerSelections}
											field={businessField}
											contactId={contact?.id}
											meta={businessField.meta ?? {}}
											questionType={CREATE_PEOPLE_BLOCK}
										/>
									) : null}

									{businessField.type === 'link' ? (
										<CustomFieldLink
											id={id}
											currentAnswer={currentAnswer}
											currentAnswerSelections={currentAnswerSelections}
											field={businessField}
											contactId={contact?.id}
											questionType={CREATE_PEOPLE_BLOCK}
											key={businessField.id}
										/>
									) : null}

									{businessField.type === 'string' ? (
										<CustomFieldText
											id={id}
											currentAnswer={currentAnswer}
											currentAnswerSelections={currentAnswerSelections}
											field={businessField}
											contactId={contact?.id}
											questionType={CREATE_PEOPLE_BLOCK}
										/>
									) : null}
								</FlexColumnSmallSpaced>
							);
						} else {
							const currentField = metaData.editFields?.[f];
							if (!currentField) return null;

							const addressType = currentField.meta?.addressTypeId;
							const value = contact
								? f.startsWith(BUSINESS_ADDRESS_FIELD) && addressType
									? currentAnswerSelections?.contacts?.find(c => c.id === contact?.id)?.[`businessAddress:${addressType}`]
									: currentAnswerSelections?.contacts?.find(c => c.id === contact?.id)?.[f]
								: '';
							return (
								<QuestionField
									key={f}
									type={f}
									id={id}
									required={currentField.meta?.required}
									tabIndex={undefined}
									description={currentField.labelOverride ?? currentField.label}
									value={value}
									onChange={c => {
										if (f.startsWith(BUSINESS_ADDRESS_FIELD) || f === BUSINESS_NAME_FIELD) {
											updateBusinessDetailsForContact(
												currentAnswer,
												currentAnswerSelections,
												contact?.id,
												answerQuestion,
												{ [f]: c, asAbove: false },
												type
											);
										} else {
											updateAnswerForContact(currentAnswer, currentAnswerSelections, contact?.id, answerQuestion, f, c, type);
										}
									}}
									meta={metaData}
									component={getRenderField(currentField.componentName)}
									titleComponent={
										f === BUSINESS_NAME_FIELD && currentAnswerSelections?.contacts?.length > 1 ? (
											<LabeledCheckbox
												key={contact?.asAbove?.toString()}
												checked={contact?.asAbove ?? false}
												onChange={(_, checked) => {
													const addressType = getBusinessAddresses(currentAnswerSelections?.businessDetails);

													updateBusinessDetailsForContact(
														currentAnswer,
														currentAnswerSelections,
														contact?.id,
														answerQuestion,
														{
															[BUSINESS_NAME_FIELD]: checked
																? (currentAnswerSelections?.businessDetails?.businessName ?? '')
																: undefined,
															...(checked
																? addressType
																: Object.fromEntries(Object.keys(addressType).map(key => [key, undefined]))),
															asAbove: checked
														},
														type
													);
												}}
												label="Same as above"
											/>
										) : null
									}
								/>
							);
						}
					})}
				<SpaceBetweenRow>
					{currentAnswerSelections?.contacts?.length > 1 && formRenderMode !== FormRenderMode.design ? (
						<Button
							startIconName="DeleteOutline"
							variant="tertiary"
							color="error"
							style={{ textTransform: 'none' }}
							onClick={async () => {
								if (eventContext?.event?.reservation) {
									const updated = await eventContext.updateReservationCount?.({
										operation: ReservationUpdate.Remove,
										count: 1
									});
									if (!updated) return;
								}

								const contacts = currentAnswerSelections?.contacts.filter(c => c.id !== contact?.id) ?? [];

								answerQuestion({
									...currentAnswer,
									answer: {
										answerJson: JSON.stringify({
											...currentAnswerSelections,
											contacts
										})
									},
									type
								});
							}}
						>
							Remove
						</Button>
					) : (
						<></>
					)}
				</SpaceBetweenRow>
			</FlexColumnSmallSpaced>
		</>
	);
};
